// ШАГ «ЧТО ЗАПУСКАЕТ» — ОДИН СТРОИТЕЛЬ НА ЦЕПОЧКУ И НА БОТОВ
// (MESSAGE-COMPOSER К18, претензия владельца П16 06.09.2026: «ты видишь два разных
// механизма, и это правда»).
//
// До 06.09 шаг жил ДВУМЯ механизмами: у ботов — списком поверх набора галочек
// (`WizardStep.list`, ТЗ В1 03.09), у цепочки — списком объектов (`WizardStep.itemList`,
// Г20/Г22/Г23). Всё, что владелец принял на цепочке — черновик, полушторка, добавление
// по «Готово», серые виды в меню, несколько ссылок, — до ботов не доезжало: код разошёлся
// на две ветки. Здесь живёт ОДИН строитель; ветка `list` снесена целиком, копипасты не
// осталось.
//
// МОДЕЛЬ ОТВЕТА (решение Фейбла, К18.3 этап 2 п.1): у ботов список триггеров — ответ
// `entries`, строка JSON `BotEntry[]` РОВНО той формы, что лежит в
// `scenario_drafts.trigger_entries` (`event`, `kind`, `keywords`, `mediaIds`,
// `linkLabel`; у Телеграма `event` всегда 'message'). У цепочки остаётся свой
// `ChainEntry[]` — у её видов «Прошёл бота» и «Получил метку» ни события, ни публикаций
// нет. Общий код читает и то, и другое через `cfg.store` и `cfg.chat`.
import {
  IG_START_EVENTS,
  paramVisible,
  type CatalogParam,
  type CatalogParamOption,
} from '@scriptera/ui/scenario';
import {
  botEntry,
  BOT_ENTRY_ALWAYS_ANY,
  TG_ENTRY_KINDS,
  type BotEntry,
  type BotEntryEvent,
  type BotEntryKind,
} from '@scriptera/db/bot-entries';
import { refLinkUrl, translitRefCode } from '@/lib/bot-ref-core';
import {
  ansEntryKinds,
  ansStr,
  ansWords,
  isWizWordsField,
  tgEntriesOf,
  tgEntryKindParams,
  wizEntryWords,
  wizKeywordsParam,
  wizLinkReadyParam,
  type WizardItemAddKind,
  type WizardItemLive,
  type WizardLocale,
  type WizardStep,
  type WizardStepItemList,
} from './core';

/** Ответ анкеты со списком триггеров — строка JSON. Имя одно у ботов и у цепочки. */
export const TRIGGER_ENTRIES_FIELD = 'entries';

/** Потолок триггеров в одной анкете: ответы живут в jsonb черновика (MAX_WIZARD_ANSWERS). */
export const TRIGGER_MAX_ENTRIES = 20;

/**
 * ЧЕЙ ЭТО ШАГ. У цепочки семь видов (два своих сверху пяти чат-видов Телеграма), у бота
 * Телеграма — те же пять, у бота Инстаграма — шесть СОБЫТИЙ сети. Больше меню ничем не
 * отличаются: страницы, черновик, «Править», «Удалить» и слова общие.
 */
export type TriggerKinds = 'chain' | 'tg' | 'ig';

/** Пара функций анкеты: прочитать список из ответов / записать список в ответы. */
export interface TriggerStore<E> {
  read: (answers: Record<string, unknown>) => E[];
  write: (answers: Record<string, unknown>, list: readonly E[]) => Record<string, unknown>;
}

/** Чат-триггер В ОБЩЕМ ВИДЕ и обратно: у цепочки свой тип элемента (ChainEntry). */
export interface TriggerChat<E> {
  /** Чат-триггер в виде BotEntry; null — это СВОЙ вид анкеты (см. TriggerOwn). */
  of: (e: E) => BotEntry | null;
  make: (e: BotEntry) => E;
}

/**
 * СВОИ ВИДЫ АНКЕТЫ СВЕРХУ ОБЩЕГО МЕНЮ — у цепочки «Прошёл бота» и «Получил метку»
 * (К9 п.2). Общий код про них ничего не знает и спрашивает эти руки.
 */
export interface TriggerOwn<E> {
  /** Ключи своих видов меню: их общий `add` не трогает. */
  keys: readonly string[];
  is: (e: E) => boolean;
  /** Виды для меню — стоят ПЕРЕД общими, порядок свой (К9 п.2). */
  kinds: (list: readonly E[]) => WizardItemAddKind[];
  /** Новый элемент своего вида; null — добавлять нечего (уже есть, бот не выбран). */
  add: (list: readonly E[], kind: string, value?: string) => E | null;
  line: (e: E, live?: WizardItemLive) => { title: string; sub: string; chevron?: boolean; invalid?: boolean };
  title: (e: E, live?: WizardItemLive) => string;
  fields: (e: E, live?: WizardItemLive) => CatalogParam[];
  values: (e: E) => Record<string, unknown>;
  apply: (e: E, patch: Record<string, unknown>) => E;
  /** Что уже занято выбором `pick` (боты у «Прошёл бота»). */
  pickTaken?: (list: readonly E[]) => string[];
}

export interface TriggerStepCfg<E> {
  kinds: TriggerKinds;
  store: TriggerStore<E>;
  chat: TriggerChat<E>;
  /** Ключ, заголовок и подводка шага — свои у каждой анкеты. */
  key: string;
  title: string;
  intro: string;
  /** Поля шага НАД списком (у цепочки — строка «Бот»); у ботов их нет. */
  params?: CatalogParam[];
  own?: TriggerOwn<E>;
  max?: number;
}

/**
 * СЛОВА ШАГА, которых нет у `wizEntryWords`: подстрока незаполненного триггера и причины
 * блокировки шага. Живут ОДНИМ местом — у цепочки и у ботов они одни и те же.
 */
export function trigWords(locale: WizardLocale): {
  empty: string;
  needWords: string;
  needLink: string;
  needPosts: string;
  dupLink: string;
} {
  const ru = locale === 'ru';
  return {
    empty: ru ? 'Не заполнено' : 'Not filled in',
    needWords: ru ? 'Добавьте хотя бы одно кодовое слово' : 'Add at least one code word',
    needLink: ru ? 'Укажите название источника ссылки' : 'Name the link source',
    // ДВЕ ССЫЛКИ С ОДНИМ ХВОСТОМ (К19.1 п.6, владелец 06.09.2026: «добавил переход по
    // ссылке такой, какой был, — стал серый экран»). Раньше вторую ссылку выкидывал сам
    // разбор списка, и её страница оставалась без своего триггера; теперь она живёт, а
    // человеку говорят словами, что не так. Слово — простое (К21.1 п.8, владелец
    // 07.09.2026: прежнее «У двух триггеров „Переход по ссылке“ одинаковый хвост
    // ссылки — измените подпись» назвал дурацким): человеку важно, что такая ссылка у
    // этого бота уже есть, а не как она устроена внутри.
    dupLink: ru ? 'Эта ссылка уже добавлена в этом боте' : 'This link is already added to this bot',
    needPosts: ru
      ? 'Выберите хотя бы одну публикацию или включите «Под всеми публикациями»'
      : 'Select at least one post or enable “Under all posts”',
  };
}

/** Слова вида запуска Телеграма — из свода анкет ботов (второго набора строк нет). */
export function trigKindOption(
  kind: BotEntryKind,
  locale: WizardLocale,
): { name: string; description: string } {
  const set = tgEntryKindParams(locale)[0];
  const opts = (set?.options ?? []) as CatalogParamOption[];
  const found = opts.find((o) => String(o.value) === kind);
  return { name: found?.name ?? kind, description: found?.description ?? '' };
}

/**
 * НАЗВАНИЯ И ПОДСКАЗКИ СОБЫТИЙ ИНСТАГРАМА (ТЗ владельца 02.09 §3.1). Живут ЗДЕСЬ, а не в
 * анкете: их читают и меню видов, и строки списка, и страница триггера.
 */
export const TRIG_EVENT_NAMES: Record<
  BotEntryEvent,
  { ru: string; en: string; hintRu: string; hintEn: string }
> = {
  message: {
    ru: 'Сообщение в директ',
    en: 'Direct message',
    hintRu: 'Пользователь написал аккаунту в личные сообщения',
    hintEn: 'A user sent a direct message to the account',
  },
  comment: {
    ru: 'Комментарий под публикацией',
    en: 'Comment under a post',
    hintRu:
      'Пользователь оставил комментарий под публикацией или Reels. Instagram разрешает отправить ему одно личное сообщение в течение 7 дней; дальнейшая переписка возможна только после его ответа',
    hintEn:
      'A user commented under a post or Reels. Instagram allows one private message within 7 days; further messages are possible only after the user replies',
  },
  liveComment: {
    ru: 'Комментарий в прямом эфире',
    en: 'Comment in a live',
    hintRu: 'Пользователь написал в чат прямого эфира. Ответ возможен только во время эфира',
    hintEn: 'A user wrote in the live chat. A reply is possible only while the live is on',
  },
  storyReply: {
    ru: 'Ответ на историю',
    en: 'Reply to a story',
    hintRu: 'Пользователь ответил на историю текстом',
    hintEn: 'A user replied to a story with text',
  },
  storyReaction: {
    ru: 'Реакция на историю',
    en: 'Reaction to a story',
    hintRu:
      'Пользователь поставил быструю реакцию на историю. Лайки и стикеры не учитываются; аккаунтам с более чем 50 000 подписчиков Instagram такие реакции не передаёт',
    hintEn:
      'A user reacted to a story with a quick reaction. Likes and stickers do not count; Instagram does not deliver these for accounts with more than 50,000 followers',
  },
  storyMention: {
    ru: 'Отметка в истории',
    en: 'Mention in a story',
    hintRu: 'Пользователь отметил аккаунт в своей истории',
    hintEn: 'A user mentioned the account in their story',
  },
};

/** Порядок событий Инстаграма — свода IG_START_EVENTS (он же порядок меню и списка). */
export const TRIG_IG_EVENTS: readonly BotEntryEvent[] = IG_START_EVENTS.map(
  (r) => r.event as BotEntryEvent,
);

/** События Инстаграма, у которых анкета отбирает по словам (у реакции и отметки — нет). */
export const TRIG_IG_WORD_EVENTS: readonly BotEntryEvent[] = [
  'message',
  'comment',
  'liveComment',
  'storyReply',
];

/** Имена полей анкеты по событию Инстаграма: у каждого свой режим и свои слова. */
export const TRIG_IG_FIELDS: Record<string, { mode: string; words: string }> = {
  message: { mode: 'messageMode', words: 'messageWords' },
  comment: { mode: 'commentMode', words: 'commentWords' },
  liveComment: { mode: 'liveMode', words: 'liveWords' },
  storyReply: { mode: 'storyReplyMode', words: 'storyReplyWords' },
};

/**
 * ТРИ ВАРИАНТА «КОГДА ЗАПУСКАТЬ» — СЛОВАМИ ПО СОБЫТИЮ (К18.3 этап 2 п.4 а, владелец
 * 06.09.2026): общий набор говорил «сообщение» даже там, где человек пишет КОММЕНТАРИЙ.
 * Формулировки — прежние («Старт», ru.json chatStartAny / chatStartKeyword /
 * chatStartExact), меняется только само существительное и согласование.
 */
export function trigModeOptions(
  locale: WizardLocale,
  event: BotEntryEvent = 'message',
): CatalogParamOption[] {
  const ru = locale === 'ru';
  const kind = event === 'comment' || event === 'liveComment' ? 'comment' : event === 'storyReply' ? 'reply' : 'msg';
  const RU = {
    msg: {
      any: 'Любое сообщение',
      keyword: 'Сообщение, которое содержит слово',
      exact: 'Определённое сообщение',
      anyD: 'Бот ответит на любое сообщение — кодовое слово не нужно.',
      keywordD: 'Бот ответит, если кодовое слово есть в сообщении — даже среди других слов.',
      exactD: 'Бот ответит, только если сообщение — ровно кодовое слово, без других слов.',
    },
    comment: {
      any: 'Любой комментарий',
      keyword: 'Комментарий, который содержит слово',
      exact: 'Определённый комментарий',
      anyD: 'Бот ответит на любой комментарий — кодовое слово не нужно.',
      keywordD: 'Бот ответит, если кодовое слово есть в комментарии — даже среди других слов.',
      exactD: 'Бот ответит, только если комментарий — ровно кодовое слово, без других слов.',
    },
    reply: {
      any: 'Любой ответ на историю',
      keyword: 'Ответ на историю, который содержит слово',
      exact: 'Определённый ответ на историю',
      anyD: 'Бот ответит на любой ответ на историю — кодовое слово не нужно.',
      keywordD: 'Бот ответит, если кодовое слово есть в ответе на историю — даже среди других слов.',
      exactD: 'Бот ответит, только если ответ на историю — ровно кодовое слово, без других слов.',
    },
  } as const;
  const EN = {
    msg: {
      any: 'Any message',
      keyword: 'Message that contains a word',
      exact: 'Exact message',
      anyD: 'The bot answers any message — no code word needed.',
      keywordD: 'The bot answers when the code word appears in the message — even among other words.',
      exactD: 'The bot answers only when the message is exactly the code word and nothing else.',
    },
    comment: {
      any: 'Any comment',
      keyword: 'Comment that contains a word',
      exact: 'Exact comment',
      anyD: 'The bot answers any comment — no code word needed.',
      keywordD: 'The bot answers when the code word appears in the comment — even among other words.',
      exactD: 'The bot answers only when the comment is exactly the code word and nothing else.',
    },
    reply: {
      any: 'Any story reply',
      keyword: 'Story reply that contains a word',
      exact: 'Exact story reply',
      anyD: 'The bot answers any story reply — no code word needed.',
      keywordD:
        'The bot answers when the code word appears in the story reply — even among other words.',
      exactD:
        'The bot answers only when the story reply is exactly the code word and nothing else.',
    },
  } as const;
  const w = ru ? RU[kind] : EN[kind];
  return [
    { name: w.any, value: 'any', description: w.anyD },
    { name: w.keyword, value: 'keyword', description: w.keywordD },
    { name: w.exact, value: 'exact', description: w.exactD },
  ];
}

/**
 * ПОЛЯ ВЫБОРА ПУБЛИКАЦИЙ (вход-комментарий Инстаграма) — ОДИН источник на страницу
 * триггера списка и на форму «Кодового слова в комментарии» (К18.3 этап 2 п.4 б):
 * второй копии полей не оставляем.
 */
export function trigPostsParams(locale: WizardLocale): CatalogParam[] {
  const ru = locale === 'ru';
  return [
    {
      name: 'postsMode',
      type: 'options',
      required: true,
      default: 'all',
      displayName: ru ? 'Публикации' : 'Posts',
      options: [
        { name: ru ? 'Под всеми публикациями' : 'Under all posts', value: 'all' },
        { name: ru ? 'Только под выбранными' : 'Only under selected', value: 'some' },
      ],
    },
    {
      name: 'mediaIds',
      type: 'string',
      required: true,
      default: '',
      displayName: ru ? 'Выбранные публикации' : 'Selected posts',
      displayOptions: { show: { postsMode: ['some'] } },
    },
  ];
}

/**
 * Слова из ответа поля-кружков: строка через запятую (формат ответов анкеты — прежний)
 * ИЛИ список (так их шлёт родной экран кружков). Правило ОДНО на всех: им же страница
 * мастера решает, пуст ли триггер с кодовым словом (`pageWarnAt`, К21.1 п.5 а) — второго
 * разбора слов по месту не заводим.
 */
export function wordsOfPatch(raw: unknown): string[] {
  const list =
    typeof raw === 'string'
      ? raw.split(',')
      : Array.isArray(raw)
        ? raw.filter((w): w is string => typeof w === 'string')
        : [];
  return [...new Set(list.map((w) => w.trim()).filter((w) => w !== ''))];
}

/** id публикаций Меты из ответа-строки: только цифры, кап 50 (как у листа «Старта»). */
function mediaOfPatch(raw: unknown): string[] {
  const list = typeof raw === 'string' ? raw.split(',') : Array.isArray(raw) ? raw.map(String) : [];
  return list
    .map((s) => String(s).trim())
    .filter((s) => /^[0-9]+$/.test(s))
    .slice(0, 50);
}

/**
 * МЯГКИЙ РАЗБОР СПИСКА ТРИГГЕРОВ ДЛЯ АНКЕТЫ (образец — `readEntries` цепочки). Отличие
 * от `parseBotEntries` одно: только что добавленный триггер ещё БЕЗ слов и без подписи
 * здесь живёт (иначе строка исчезала бы сразу после добавления), а в базу он не уедет —
 * шаг заблокирован (`trigBlocked`).
 */
export function readTrigEntries(raw: string, kinds: TriggerKinds, max = TRIGGER_MAX_ENTRIES): BotEntry[] {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  const ig = kinds === 'ig';
  const out: BotEntry[] = [];
  for (const x of v) {
    if (typeof x !== 'object' || x === null) continue;
    const o = x as Record<string, unknown>;
    if (ig) {
      const event = o.event;
      if (!TRIG_IG_EVENTS.includes(event as BotEntryEvent)) continue;
      const ev = event as BotEntryEvent;
      // Событие в списке ОДИН раз: второй такой же триггер — тот же вход дважды.
      if (out.some((e) => e.event === ev)) continue;
      const always = (BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(ev);
      const rawKind = o.kind;
      const kind: BotEntryKind =
        always || rawKind === 'any' ? 'any' : rawKind === 'exact' ? 'exact' : rawKind === 'keyword' ? 'keyword' : 'any';
      out.push(
        botEntry(ev, kind, {
          keywords: wordsOfPatch(o.keywords),
          mediaIds: mediaOfPatch(o.mediaIds),
        }),
      );
    } else {
      const kind = o.kind;
      if (!(TG_ENTRY_KINDS as readonly string[]).includes(kind as string)) continue;
      const k = kind as BotEntryKind;
      // Вид один на бота — КРОМЕ «Перехода по ссылке» (К18.1 п.5): ссылок сколько
      // угодно, одинаковым не бывает их ХВОСТ.
      if (k !== 'link' && out.some((e) => e.kind === k)) continue;
      if (k === 'link') {
        // ДУБЛЬ ХВОСТА ЗДЕСЬ НЕ ВЫКИДЫВАЕТСЯ (К19.1 п.6, владелец 06.09.2026): пока
        // человек набирает подпись, она на миг совпадает с уже заведённой — и триггер
        // исчезал из списка прямо во время набора, оставляя открытую страницу пустой.
        // Список анкеты хранит обе ссылки как есть; про совпадение говорит `trigBlocked`
        // и подсказка-ошибка под полем, а в базу такой список не уедет (строгий разбор
        // `parseBotEntries` — без правок).
        const linkLabel = typeof o.linkLabel === 'string' ? o.linkLabel.trim().slice(0, 120) : '';
        out.push(botEntry('message', 'link', { linkLabel }));
      } else if (k === 'exact' || k === 'keyword') {
        out.push(botEntry('message', k, { keywords: wordsOfPatch(o.keywords) }));
      } else {
        out.push(botEntry('message', k));
      }
    }
    if (out.length >= max) break;
  }
  return out;
}

/**
 * СТАРЫЙ ЧЕРНОВИК ТЕЛЕГРАМА → СПИСОК ТРИГГЕРОВ (К18.3 этап 2 п.2). До 06.09 запуск жил
 * набором галочек `entryKinds` и плоскими полями `exactWords`/`keywordWords`/`linkLabel`;
 * такие черновики лежат в базе с 02.09 и обязаны открываться без потерь. Назад пишется
 * только `entries` — тот же адаптер, что у цепочки (`aud1` → `entries`, Г20).
 */
export function tgLegacyEntries(answers: Record<string, unknown>): BotEntry[] {
  // НОВЫЙ ЧЕРНОВИК РОЖДАЕТСЯ БЕЗ ТРИГГЕРА (К19.1 п.8, владелец 06.09.2026: «в „Подарке
  // за подписку“ что в инсте, что в телеге по умолчанию стоит — давай уберём»): ключа
  // `entryKinds` в ответах нет вовсе — списка тоже нет, человек добавляет сам, как у
  // цепочки. У СТАРОГО черновика ключ есть, и его раскладывает прежнее правило
  // (`ansEntryKinds`: пустой набор галочек = «Команда /start»).
  if (answers.entryKinds === undefined) return [];
  return tgEntriesOf(ansEntryKinds(answers), {
    exact: ansWords(answers, 'exactWords'),
    keyword: ansWords(answers, 'keywordWords'),
    linkLabel: ansStr(answers, 'linkLabel', 120),
  });
}

/**
 * ХРАНИЛИЩЕ СПИСКА ТРИГГЕРОВ БОТА: ответ `entries` (строка JSON `BotEntry[]`), а нет
 * его — адаптер старого черновика. Обратно пишется ТОЛЬКО `entries`.
 */
export function botTrigStore(
  kinds: TriggerKinds,
  legacy: (answers: Record<string, unknown>) => BotEntry[],
): TriggerStore<BotEntry> {
  return {
    read: (answers) => {
      const raw = ansStr(answers, TRIGGER_ENTRIES_FIELD, 8000);
      return raw !== '' ? readTrigEntries(raw, kinds) : legacy(answers);
    },
    write: (answers, list) => ({ ...answers, [TRIGGER_ENTRIES_FIELD]: JSON.stringify(list) }),
  };
}

/** У ботов элемент списка И ЕСТЬ чат-триггер — переводить нечего (у цепочки иначе). */
export const botTrigChat: TriggerChat<BotEntry> = { of: (e) => e, make: (e) => e };

/** Новый список с добавленным чат-триггером; null — добавлять нечего (дубль вида). */
export function trigAdd(
  kinds: TriggerKinds,
  list: readonly BotEntry[],
  key: string,
): BotEntry | null {
  if (kinds === 'ig') {
    if (!TRIG_IG_EVENTS.includes(key as BotEntryEvent)) return null;
    const ev = key as BotEntryEvent;
    if (list.some((e) => e.event === ev)) return null;
    const always = (BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(ev);
    // Ответ на историю запускает бота обычно на ЛЮБОЙ ответ; у остальных умолчание —
    // «содержит слово» (то же правило, что было у полей анкеты).
    const mode: BotEntryKind = always || ev === 'storyReply' ? 'any' : 'keyword';
    return botEntry(ev, mode);
  }
  if (!(TG_ENTRY_KINDS as readonly string[]).includes(key)) return null;
  const k = key as BotEntryKind;
  // НОВАЯ ССЫЛКА ЗАВОДИТСЯ ВСЕГДА (К18.1 п.5): она ещё пустая, хвоста у неё нет, а
  // дублем считается одинаковый хвост (readTrigEntries).
  if (k !== 'link' && list.some((e) => e.kind === k)) return null;
  return botEntry('message', k);
}

/**
 * ВИДЫ ДЛЯ МЕНЮ «ДОБАВИТЬ ТРИГГЕР». Уже добавленный вид ИЗ МЕНЮ НЕ УХОДИТ (К18.1 п.3):
 * он остаётся на своём месте серым, с причиной «Уже добавлено». «Переход по ссылке» не
 * гасится НИКОГДА: ссылок бывает сколько угодно.
 */
export function trigAddKinds(
  kinds: TriggerKinds,
  locale: WizardLocale,
  list: readonly BotEntry[],
): WizardItemAddKind[] {
  const w = wizEntryWords(locale);
  const ru = locale === 'ru';
  if (kinds === 'ig') {
    return TRIG_IG_EVENTS.map((event) => {
      const n = TRIG_EVENT_NAMES[event];
      const taken = list.some((e) => e.event === event);
      const always = (BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(event);
      return {
        key: event,
        title: ru ? n.ru : n.en,
        description: ru ? n.hintRu : n.hintEn,
        // Отбора по словам у реакции и отметки нет вовсе — значит и страницы нет:
        // триггер добавляется тапом по виду и живёт строкой (К16.1 П5).
        ...(always ? { direct: true } : {}),
        ...(taken ? { disabled: true, disabledHint: w.already } : {}),
      };
    });
  }
  return TG_ENTRY_KINDS.map((kind) => {
    const opt = trigKindOption(kind, locale);
    const taken = kind !== 'link' && list.some((e) => e.kind === kind);
    return {
      key: kind,
      title: opt.name,
      ...(opt.description ? { description: opt.description } : {}),
      // Настроек нет — добавляем сразу, без страницы (К16.0 п.4, К16.1 П5).
      ...(kind === 'start' || kind === 'any' ? { direct: true } : {}),
      // «Переход по ссылке» — ПОЛНОЙ шторкой (К18.1 п.4 п.1): настроек много.
      ...(kind === 'link' ? { full: true } : {}),
      ...(taken ? { disabled: true, disabledHint: w.already } : {}),
    };
  });
}

/** Заголовок страницы триггера: имя вида (Телеграм) или имя события (Инстаграм). */
export function trigTitle(kinds: TriggerKinds, locale: WizardLocale, e: BotEntry): string {
  if (kinds === 'ig') {
    const n = TRIG_EVENT_NAMES[e.event];
    return locale === 'ru' ? n.ru : n.en;
  }
  return trigKindOption(e.kind, locale).name;
}

/**
 * ЗАГОЛОВОК СТРАНИЦЫ ТРИГГЕРА — КОРОЧЕ, ЧЕМ ИМЯ В МЕНЮ (К20.1 п.8, владелец 07.09.2026:
 * «„Сообщение, которое содержит слово“ не помещается в заголовок полушторки, некрасиво»).
 * В шапку листа влезает одна строка, переносов у Apple в шапках листов не бывает — значит
 * страница зовётся короче. Имя вида в МЕНЮ и в СТРОКЕ СПИСКА остаётся полным: там человек
 * выбирает из нескольких похожих, и обрезать их нельзя.
 */
export function trigPageTitle(kinds: TriggerKinds, locale: WizardLocale, e: BotEntry): string {
  if (kinds !== 'ig' && e.kind === 'keyword') {
    return locale === 'ru' ? 'Содержит слово' : 'Contains a word';
  }
  return trigTitle(kinds, locale, e);
}

/**
 * СТРОКА СПИСКА: заголовок и подстрока словами. У триггера без настроек подстроки нет
 * вовсе и за строкой НЕТ страницы (К16.0 п.3, претензия владельца П5).
 */
export function trigLine(
  kinds: TriggerKinds,
  locale: WizardLocale,
  e: BotEntry,
  live?: WizardItemLive,
): { title: string; sub: string; chevron?: boolean; invalid?: boolean } {
  const t = trigWords(locale);
  const title = trigTitle(kinds, locale, e);
  if (kinds === 'ig') {
    if ((BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(e.event)) {
      return { title, sub: '', chevron: false };
    }
    const mode = trigModeOptions(locale, e.event).find((o) => String(o.value) === e.kind);
    const words = e.keywords.join(', ');
    // ПУСТОЙ ТРИГГЕР ВИДЕН СРАЗУ (К20.1 п.14, владелец 07.09.2026: «добавил триггеры, не
    // указав ни одного кодового слова»): у Телеграма о таком говорит «Не заполнено», а у
    // Инстаграма строка показывала ОДИН режим — и заполненный триггер от пустого было не
    // отличить. Правило одно на всех: слов нет (и они нужны) — «режим · Не заполнено».
    if (words !== '') return { title, sub: `${mode?.name ?? ''} · ${words}` };
    const needs = e.kind !== 'any';
    return { title, sub: needs ? `${mode?.name ?? ''} · ${t.empty}` : (mode?.name ?? '') };
  }
  if (e.kind === 'exact' || e.kind === 'keyword') {
    return { title, sub: e.keywords.length > 0 ? e.keywords.join(', ') : t.empty };
  }
  if (e.kind === 'link') {
    // ПОДСТРОКА — САМА ССЫЛКА ЦЕЛИКОМ (К19.1 п.5, владелец 06.09.2026: «с серым нужно
    // целую ссылку было написать, она помещается»). Логин бота знает страница мастера и
    // подкладывает его живыми списками (`WizardItemLive.botHandle`); нет логина или нет
    // хвоста — остаётся прежняя подстрока (подпись источника либо «Не заполнено»).
    const handle = (live?.botHandle ?? '').replace(/^@/, '');
    const tail = translitRefCode(e.linkLabel);
    if (handle !== '' && tail !== '') return { title, sub: refLinkUrl(handle, tail) };
    return { title, sub: e.linkLabel || t.empty };
  }
  return { title, sub: '', chevron: false };
}

/**
 * ПОЛЯ СТРАНИЦЫ ТРИГГЕРА. У Телеграма — кодовые слова либо готовая ссылка с подписью
 * источника; у Инстаграма — «Когда запускать» словами СВОЕГО события плюс кодовые слова,
 * а у комментария ещё и публикации. `displayOptions` по набору галочек тут не нужны: на
 * странице стоит РОВНО ОДИН триггер.
 */
export function trigFields(
  kinds: TriggerKinds,
  locale: WizardLocale,
  e: BotEntry,
): CatalogParam[] {
  if (kinds === 'ig') {
    if ((BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(e.event)) return [];
    const f = TRIG_IG_FIELDS[e.event];
    if (!f) return [];
    const ru = locale === 'ru';
    const out: CatalogParam[] = [
      {
        name: f.mode,
        type: 'options',
        required: true,
        default: 'keyword',
        displayName: ru ? 'Когда запускать' : 'When to start',
        options: trigModeOptions(locale, e.event),
      },
      wizKeywordsParam(locale, { [f.mode]: ['keyword', 'exact'] }, f.words),
    ];
    // Публикации — часть настройки триггера-комментария (и только его).
    return e.event === 'comment' ? [...out, ...trigPostsParams(locale)] : out;
  }
  if (e.kind === 'exact' || e.kind === 'keyword') {
    return [wizKeywordsParam(locale, {}, e.kind === 'exact' ? 'exactWords' : 'keywordWords')];
  }
  if (e.kind === 'link') {
    const link = tgEntryKindParams(locale).find((p) => p.name === 'linkLabel');
    if (!link) return [];
    const { displayOptions: _hidden, ...rest } = link;
    // САМА ССЫЛКА — ПЕРВОЙ, «Откуда придут люди» под ней (К17.4 п.6).
    return [wizLinkReadyParam(locale), rest];
  }
  // «Команда /start» и «Любое сообщение» — БЕЗ СТРАНИЦЫ ВОВСЕ (К16.1 П5).
  return [];
}

/** Режим публикаций триггера-комментария: ответ анкеты, а нет его — по самому триггеру. */
function postsModeOf(e: BotEntry, answers: Record<string, unknown>): string {
  const raw = ansStr(answers, 'postsMode', 8, '');
  if (raw === 'all' || raw === 'some') return raw;
  return e.mediaIds.length > 0 ? 'some' : 'all';
}

/** Значения полей страницы триггера (их место — внутри ответа-списка, не плоско). */
export function trigValues(
  kinds: TriggerKinds,
  e: BotEntry,
  answers: Record<string, unknown>,
): Record<string, unknown> {
  if (kinds === 'ig') {
    const f = TRIG_IG_FIELDS[e.event];
    if (!f) return {};
    const out: Record<string, unknown> = {
      [f.mode]: e.kind,
      [f.words]: e.keywords.join(', '),
    };
    if (e.event === 'comment') {
      out.postsMode = postsModeOf(e, answers);
      out.mediaIds = e.mediaIds.join(',');
    }
    return out;
  }
  if (e.kind === 'exact') return { exactWords: e.keywords.join(', ') };
  if (e.kind === 'keyword') return { keywordWords: e.keywords.join(', ') };
  if (e.kind === 'link') return { linkLabel: e.linkLabel };
  return {};
}

/** Правка страницы триггера — НОВЫЙ триггер (иммутабельность, запрет №3). */
export function trigApply(
  kinds: TriggerKinds,
  e: BotEntry,
  patch: Record<string, unknown>,
  answers: Record<string, unknown>,
): BotEntry {
  if (kinds === 'ig') {
    const f = TRIG_IG_FIELDS[e.event];
    if (!f) return e;
    const rawMode = f.mode in patch ? patch[f.mode] : e.kind;
    const kind: BotEntryKind = rawMode === 'any' ? 'any' : rawMode === 'exact' ? 'exact' : 'keyword';
    const keywords = f.words in patch ? wordsOfPatch(patch[f.words]) : e.keywords;
    const mode = 'postsMode' in patch ? String(patch.postsMode) : postsModeOf(e, answers);
    const media = 'mediaIds' in patch ? mediaOfPatch(patch.mediaIds) : e.mediaIds;
    return botEntry(e.event, kind, {
      keywords: kind === 'any' ? [] : keywords,
      mediaIds: e.event === 'comment' && mode === 'some' ? media : [],
    });
  }
  if (e.kind === 'exact' || e.kind === 'keyword') {
    const field = e.kind === 'exact' ? 'exactWords' : 'keywordWords';
    if (!(field in patch)) return e;
    return botEntry('message', e.kind, { keywords: wordsOfPatch(patch[field]) });
  }
  if (e.kind === 'link') {
    if (!('linkLabel' in patch)) return e;
    const raw = patch.linkLabel;
    return botEntry('message', 'link', {
      linkLabel: typeof raw === 'string' ? raw.trim().slice(0, 120) : '',
    });
  }
  return e;
}

/**
 * ХВОСТ ЭТОЙ ПОДПИСИ ПОВТОРЯЕТ БОЛЕЕ РАННЮЮ ССЫЛКУ СПИСКА? (К19.1 п.6, владелец
 * 06.09.2026.) Правило ОДНО на всё: по нему шаг говорит `dupLink`, страница ссылки
 * рисует подсказку-ошибку под полем «Откуда придут люди», а «Готово» у черновика не
 * применяет правку и не закрывает страницу. Пустой хвост дублем не считается — про
 * незаполненную подпись говорят отдельно (`needLink`).
 */
export function trigDupTail(earlierLabels: readonly string[], label: string): boolean {
  const tail = translitRefCode(label);
  if (tail === '') return false;
  return earlierLabels.some((l) => translitRefCode(l) === tail);
}

/**
 * ТРИГГЕР С КОДОВЫМ СЛОВОМ, А СЛОВА НЕТ (К21.1 п.5 а, владелец 07.09.2026: «страницу
 * можно закрыть по „Готово“ без единого слова, и триггер сохраняется пустым — нужна
 * проверка»). Правило ОДНО и живёт здесь: по нему страница мастера (`pageWarnAt`) рисует
 * подсказку-ошибку под полем слов и не пускает «Готово», а шаг теми же словами держит
 * «Дальше» (`trigBlocked` → `needWords`).
 *
 * Знание «какое поле — слова» берётся по ИМЕНИ (`isWizWordsField` — одно понятие на весь
 * мастер), а «нужны ли слова при нынешнем режиме» — из `displayOptions` самого поля тем
 * же вычислителем, что рисует форму (`paramVisible`): у Телеграма поле слов стоит на
 * странице вида «Определённое сообщение»/«Содержит слово» безусловно, у Инстаграма —
 * только при `keyword|exact` из живого значения «Когда запускать» (`TRIG_IG_FIELDS`).
 * Второго списка имён и второго правила режима по месту не заводим.
 */
export function trigWordsWarn(
  params: readonly CatalogParam[],
  values: Record<string, unknown>,
  locale: WizardLocale,
): Record<string, string> | undefined {
  const defs = new Map(params.map((p) => [p.name, p.default]));
  for (const p of params) {
    if (!isWizWordsField(p.name)) continue;
    if (!paramVisible(p, values, defs)) continue;
    if (wordsOfPatch(values[p.name]).length === 0) {
      return { [p.name]: trigWords(locale).needWords };
    }
  }
  return undefined;
}

/**
 * ПОЛОВИНЧАТЫЙ ТРИГГЕР НЕ ПУСКАЕТ ДАЛЬШЕ. Слова и подпись источника живут на СВОЕЙ
 * странице, обязательность поля шага их больше не ловит — говорим словами и держим шаг
 * (иначе в базу уехал бы список, который `parseBotEntries` отобьёт 400).
 */
export function trigBlocked(
  kinds: TriggerKinds,
  locale: WizardLocale,
  list: readonly BotEntry[],
  answers: Record<string, unknown>,
): string | null {
  const t = trigWords(locale);
  if (list.some((e) => (e.kind === 'exact' || e.kind === 'keyword') && e.keywords.length === 0)) {
    return t.needWords;
  }
  if (list.some((e) => e.kind === 'link' && e.linkLabel.trim() === '')) return t.needLink;
  // ДВЕ ССЫЛКИ С ОДНИМ ХВОСТОМ (К19.1 п.6): разбор списка их больше не выкидывает —
  // значит шаг обязан сказать об этом словами и не пустить дальше (в базу такой список
  // не уедет: строгий `parseBotEntries` считает дублем тот же хвост).
  const seen: string[] = [];
  for (const e of list) {
    if (e.kind !== 'link') continue;
    if (trigDupTail(seen, e.linkLabel)) return t.dupLink;
    seen.push(e.linkLabel);
  }
  if (kinds === 'ig') {
    const comment = list.find((e) => e.event === 'comment');
    if (comment && postsModeOf(comment, answers) === 'some' && comment.mediaIds.length === 0) {
      return t.needPosts;
    }
  }
  return null;
}

/**
 * ШАГ «ЧТО ЗАПУСКАЕТ» — ОДИН НА ВСЕХ (К18.1 п.9–10). Строки триггеров плюс пунктирная
 * «Добавить триггер»; тап по строке открывает её страницу, добавление — по «Готово»,
 * удаление — режимом «Править» или смахиванием. Перестановки НЕТ вовсе (`canMove:false`):
 * порядок триггеров ничего не значит — сработал любой.
 */
export function triggerStep<E>(locale: WizardLocale, cfg: TriggerStepCfg<E>): WizardStep {
  const w = wizEntryWords(locale);
  const own = cfg.own;
  const max = cfg.max ?? TRIGGER_MAX_ENTRIES;
  /** Чат-триггеры списка в общем виде — по ним считаются меню и дубли. */
  const chats = (list: readonly E[]): BotEntry[] =>
    list.map((e) => cfg.chat.of(e)).filter((e): e is BotEntry => e !== null);

  const item: WizardStepItemList = {
    max,
    itemAdd: w.add,
    itemEmpty: w.empty,
    itemRemove: w.remove,
    itemTitle: (answers, i, live) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e) return '';
      if (own?.is(e)) return own.title(e, live);
      const chat = cfg.chat.of(e);
      // Заголовок СТРАНИЦЫ короче имени вида (К20.1 п.8) — в шапку листа влезает строка.
      return chat ? trigPageTitle(cfg.kinds, locale, chat) : '';
    },
    count: (answers) => cfg.store.read(answers).length,
    line: (answers, i, live) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e) return { title: '', sub: '' };
      if (own?.is(e)) return own.line(e, live);
      const chat = cfg.chat.of(e);
      return chat ? trigLine(cfg.kinds, locale, chat, live) : { title: '', sub: '' };
    },
    fields: (answers, i, live) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e) return [];
      if (own?.is(e)) return own.fields(e, live);
      const chat = cfg.chat.of(e);
      return chat ? trigFields(cfg.kinds, locale, chat) : [];
    },
    fieldValues: (answers, i) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e) return {};
      if (own?.is(e)) return own.values(e);
      const chat = cfg.chat.of(e);
      return chat ? trigValues(cfg.kinds, chat, answers) : {};
    },
    applyFields: (answers, i, patch) => {
      const list = cfg.store.read(answers);
      const cur = list[i - 1];
      if (!cur) return answers;
      // РЕЖИМ ПУБЛИКАЦИЙ — плоский ответ (он же у формы «Кодового слова в комментарии»):
      // «Только под выбранными» без единой публикации обязан жить до выбора, иначе
      // переключатель отскакивал бы назад.
      const base =
        cfg.kinds === 'ig' && 'postsMode' in patch
          ? { ...answers, postsMode: String(patch.postsMode) }
          : answers;
      const next = own?.is(cur)
        ? own.apply(cur, patch)
        : (() => {
            const chat = cfg.chat.of(cur);
            return chat ? cfg.chat.make(trigApply(cfg.kinds, chat, patch, base)) : cur;
          })();
      return cfg.store.write(
        base,
        list.map((e, idx) => (idx === i - 1 ? next : e)),
      );
    },
    addKinds: (answers) => {
      const list = cfg.store.read(answers);
      return [...(own?.kinds(list) ?? []), ...trigAddKinds(cfg.kinds, locale, chats(list))];
    },
    add: (answers, kind, value) => {
      const list = cfg.store.read(answers);
      if (list.length >= max) return answers;
      if (own && kind !== undefined && own.keys.includes(kind)) {
        const made = own.add(list, kind, value);
        return made === null ? answers : cfg.store.write(answers, [...list, made]);
      }
      const chat = trigAdd(cfg.kinds, chats(list), kind ?? '');
      return chat === null ? answers : cfg.store.write(answers, [...list, cfg.chat.make(chat)]);
    },
    remove: (answers, i) => {
      const list = cfg.store.read(answers);
      if (i < 1 || i > list.length) return answers;
      return cfg.store.write(
        answers,
        list.filter((_, idx) => idx !== i - 1),
      );
    },
    ...(own?.pickTaken
      ? { pickTaken: (answers: Record<string, unknown>) => own.pickTaken!(cfg.store.read(answers)) }
      : {}),
    // СТРАНИЦА ТРИГГЕРА — ПОЛУШТОРКОЙ, кроме «Перехода по ссылке» (К19.1 п.1–3, владелец
    // 06.09.2026: «редактирование должно тоже на половинку»). Полной остаётся только
    // ссылка: у неё готовая ссылка, поле источника и раздел «Ссылки из ваших ботов»
    // (К18.1 п.4 п.1). Свои виды цепочки («Прошёл бота», «Получил метку») — полушторкой.
    pageFull: (answers, i) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e || own?.is(e)) return false;
      const chat = cfg.chat.of(e);
      if (!chat) return false;
      // СОБЫТИЯ ИНСТАГРАМА С НАСТРОЙКАМИ — ТОЖЕ ПОЛНОЙ ВЫСОТЫ (К20.1 п.11, владелец
      // 07.09.2026: «нажимаю „Сообщение в директ“ — шторка увеличивается до большой и
      // приезжает сбоку следующее»): на странице режим запуска, кодовые слова, а у
      // комментария ещё и публикации. Реакция и отметка страницы не имеют вовсе.
      if (cfg.kinds === 'ig') {
        return !(BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(chat.event);
      }
      return chat.kind === 'link';
    },
    // «ПЕРЕХОД ПО ССЫЛКЕ» — БЕЗ КАРЕТКИ В ПОЛЕ МЕТКИ (п.28): страница открывается целиком, видны ссылка и «Ссылки из
    // ваших ботов»; клавиатура — по тапу.
    pageNoFocus: (answers, i) => {
      const e = cfg.store.read(answers)[i - 1];
      if (!e || own?.is(e)) return false;
      return cfg.chat.of(e)?.kind === 'link';
    },
    canMove: false,
  };

  return {
    key: cfg.key,
    title: cfg.title,
    intro: cfg.intro,
    params: cfg.params ?? [],
    itemList: item,
    // ТЕЛЕФОНЧИКА ЗДЕСЬ НЕТ (К19.1 п.9, владелец 06.09.2026): в шапке уже стоит
    // «Править», две кнопки лишние, а превью на этом шаге не показывает ничего сверх
    // того, что и так видно строками. На остальных шагах телефончик остаётся.
    noPreview: true,
    // Шаг запуска: сверка занятости на «Далее» и возврат сюда по «Поменять запуск» (П160).
    starts: true,
  };
}
